import React, { useEffect, useState } from 'react';
import {
  LuSmartphone, LuDownload, LuQrCode, LuPlug, LuSend, LuPlay,
  LuPackageCheck, LuShieldCheck, LuBellRing, LuBatteryCharging, LuLink2, LuCheck, LuCloud
} from 'react-icons/lu';
import useInView from '../hooks/useInView';

/*
 * Five steps, because the previous four hid the biggest activation killer:
 * sideloading the APK. The landing page should own the friction, not gloss
 * over it. The sideload step gets its own card AND a preview of the install
 * dialog so users know exactly what they're in for before they sign up.
 */
const STEPS = [
  { n: 1, Icon: LuSmartphone, title: 'Grab a spare Android',  body: 'Any phone with a SIM slot works. No spare? A $40 used phone is fine.' },
  { n: 2, Icon: LuDownload,   title: 'Sideload the APK',      body: 'Open textlinksms.com/textlink.apk, enable "unknown sources," tap install. 30 seconds.' },
  { n: 3, Icon: LuQrCode,     title: 'Scan the pairing QR',   body: 'Open the app, scan the code in your dashboard. SIM shows up instantly.' },
  { n: 4, Icon: LuPlug,       title: 'Connect your stack',    body: 'GoHighLevel, Zapier, or our REST API. No weeks of A2P paperwork.' },
  { n: 5, Icon: LuSend,       title: 'Send at carrier cost',  body: 'From $0.001/SMS through your own SIM. Up to 95% less than Twilio.' }
];

const PrereqCallout = () => (
  <div className="hiw-prereq">
    <div className="hiw-prereq-tick">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
    </div>
    <div className="hiw-prereq-text">
      <strong>Got a spare Android? You're ready.</strong>
      <span>No spare? A used phone for $40–60 works great — see our <a href="/recommended-setup">guide</a>.</span>
    </div>
  </div>
);

const CHECKLIST = [
  { icon: LuPackageCheck, title: 'APK installed',                sub: 'textlink.apk · 36 MB',             time: '0:00' },
  { icon: LuShieldCheck,  title: 'Unknown sources enabled',      sub: 'Settings → Security',              time: '0:15' },
  { icon: LuBellRing,     title: 'SMS permissions granted',      sub: 'Required to send & receive',       time: '0:42' },
  { icon: LuBatteryCharging, title: 'Battery optimization off',  sub: 'Keeps the app alive in background',time: '1:10' },
  { icon: LuLink2,        title: 'Paired with TextLink',         sub: 'Scanned QR from dashboard',        time: '1:28' }
];

const STEP_MS = 900;      // gap between each item becoming active
const ACTIVE_MS = 650;    // how long each item shows spinner before completing
const HOLD_MS = 2800;     // pause with all done before resetting

const InstallPreview = () => {
  // step === -1: all pending; step === i: item i is currently active (spinner);
  // items with index < step are done; items with index > step are pending.
  const [step, setStep] = useState(-1);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    const timers = [];
    CHECKLIST.forEach((_, i) => {
      timers.push(setTimeout(() => setStep(i), i * STEP_MS));
      timers.push(setTimeout(() => setStep(i + 0.5), i * STEP_MS + ACTIVE_MS));
    });
    const totalMs = (CHECKLIST.length - 1) * STEP_MS + ACTIVE_MS + HOLD_MS;
    timers.push(setTimeout(() => {
      setStep(-1);
      setCycle((c) => c + 1);
    }, totalMs));
    return () => timers.forEach(clearTimeout);
  }, [cycle]);

  const doneCount = step < 0 ? 0 : Math.min(CHECKLIST.length, Math.floor(step) + (step % 1 === 0.5 ? 1 : 0));

  return (
    <div className="hiw-preview" role="img" aria-label="Android setup sequence">
      <div className="hiw-preview-label">
        <LuPlay size={11} strokeWidth={2} fill="currentColor" />
        What actually happens during setup
      </div>

      <div className="hiw-setup-card">
        <header className="hiw-setup-head">
          <div>
            <div className="hiw-setup-title">Device setup</div>
            <div className="hiw-setup-sub">~2 minutes · One-time per phone</div>
          </div>
          <div className="hiw-setup-progress">
            <div className="hiw-setup-progress-track">
              <div
                className="hiw-setup-progress-fill"
                style={{ width: `${(doneCount / CHECKLIST.length) * 100}%` }}
              />
            </div>
            <div className="hiw-setup-progress-label">{doneCount}/{CHECKLIST.length}</div>
          </div>
        </header>

        <ul className="hiw-checklist">
          {CHECKLIST.map((item, i) => {
            const Icon = item.icon;
            const state =
              step === -1 ? 'pending'
              : Math.floor(step) > i || (Math.floor(step) === i && step % 1 === 0.5) ? 'done'
              : Math.floor(step) === i ? 'active'
              : 'pending';
            return (
              <li key={i} className={`hiw-check hiw-check--${state}`}>
                <div className="hiw-check-icon">
                  <Icon size={14} strokeWidth={2} />
                </div>
                <div className="hiw-check-body">
                  <div className="hiw-check-title">{item.title}</div>
                  <div className="hiw-check-sub">{item.sub}</div>
                </div>
                <div className="hiw-check-status">
                  <span className="hiw-check-ring" aria-hidden="true" />
                  <span className="hiw-check-spinner" aria-hidden="true" />
                  <span className="hiw-check-done">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                </div>
                <div className="hiw-check-time">{item.time}</div>
              </li>
            );
          })}
        </ul>

      {/* Connection diagram */}
      <div className="hiw-conn">
        <div className="hiw-conn-node hiw-conn-node--device">
          <div className="hiw-conn-icon"><LuSmartphone size={16} strokeWidth={1.75} /></div>
          <div className="hiw-conn-text">
            <div className="hiw-conn-label">Your phone</div>
            <div className="hiw-conn-meta">Pixel · +1 (555) 123-4567</div>
          </div>
        </div>

        <div className="hiw-conn-wire" aria-hidden="true">
          <div className="hiw-conn-packet hiw-conn-packet-1" />
          <div className="hiw-conn-packet hiw-conn-packet-2" />
          <div className="hiw-conn-packet hiw-conn-packet-3" />
        </div>

        <div className="hiw-conn-node hiw-conn-node--cloud">
          <div className="hiw-conn-icon hiw-conn-icon--tl">
            <img src="/assets/img/logo/logo2.png" alt="" className="hiw-conn-logo" />
          </div>
          <div className="hiw-conn-text">
            <div className="hiw-conn-label">TextLink dashboard</div>
            <div className="hiw-conn-meta">
              <span className="hiw-conn-status">
                <span className="hiw-conn-dot" />
                Paired &amp; live
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

      <p className="hiw-preview-caption">
        Android asks for a few permissions — because you're installing outside the Play Store. Each step takes under 20 seconds, and we guide you through it on screen.
      </p>
    </div>
  );
};

export default function HomeHowItWorks() {
  const [ref, inView] = useInView();
  return (
    <section id="how-it-works" ref={ref} className={`hiw${inView ? ' in-view' : ''}`}>
      <div className="hiw-container">
        <header className="hiw-head">
          <span className="hiw-eyebrow">HOW IT WORKS</span>
          <h2 className="hiw-title">From ad&nbsp;click to first&nbsp;send in five&nbsp;steps.</h2>
          <p className="hiw-sub">No surprises, no paperwork. Just the real flow — including the one step most vendors hide.</p>
        </header>

        <PrereqCallout />

        <ol className="hiw-flow" aria-label="Setup steps">
          <div className="hiw-flow-line" aria-hidden="true" />
          {STEPS.map((s, i) => {
            const last = i === STEPS.length - 1;
            const Icon = s.Icon;
            return (
              <li
                key={s.n}
                className={`hiw-card${last ? ' hiw-card--last' : ''}`}
                style={{ '--i': i }}
              >
                <div className="hiw-card-top">
                  <span className="hiw-num">{String(s.n).padStart(2, '0')}</span>
                  <span className="hiw-icon"><Icon size={14} strokeWidth={1.75} /></span>
                </div>
                <div className="hiw-step-body">
                  <p className="hiw-step-title">{s.title}</p>
                  <p className="hiw-step-sub">{s.body}</p>
                </div>
              </li>
            );
          })}
        </ol>

        <InstallPreview />
      </div>

      <style jsx>{`
        .hiw {
          background: #ffffff;
          padding: clamp(6rem, 14vh, 11rem) clamp(1rem, 3vw, 2rem);
          font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: #000;
          -webkit-font-smoothing: antialiased;
          letter-spacing: -0.005em;
        }
        .hiw-container { max-width: 1200px; margin: 0 auto; }

        .hiw-head { margin-bottom: clamp(2.5rem, 5vh, 3.5rem); max-width: 780px; }
        .hiw-eyebrow {
          display: inline-block;
          font-size: 0.6875rem;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: rgba(0,0,0,0.5);
          margin-bottom: 1.25rem;
        }
        .hiw-title {
          font-size: clamp(2rem, 4.8vw, 3.25rem);
          font-weight: 500;
          letter-spacing: -0.035em;
          line-height: 1.05;
          margin: 0 0 0.875rem;
          text-align: left;
          color: #0a0a0a;
        }
        .hiw-sub {
          font-size: clamp(0.9375rem, 1.4vw, 1.0625rem);
          color: rgba(0,0,0,0.55);
          margin: 0;
          line-height: 1.55;
          max-width: 560px;
        }

        /* --- prereq callout: restrained pill, not a gradient card --- */
        .hiw-prereq {
          display: inline-flex;
          align-items: center;
          gap: 0.625rem;
          padding: 0.5rem 0.875rem 0.5rem 0.5rem;
          background: #ffffff;
          border: 1px solid rgba(0,0,0,0.08);
          border-radius: 999px;
          margin-bottom: clamp(2.5rem, 5vh, 3.5rem);
        }
        .hiw-prereq-tick {
          width: 22px; height: 22px;
          border-radius: 50%;
          background: #0a0a0a;
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .hiw-prereq-tick svg { width: 10px; height: 10px; }
        .hiw-prereq-text {
          display: inline-flex;
          align-items: baseline;
          gap: 0.5rem;
          font-size: 0.8125rem;
          line-height: 1.4;
          white-space: nowrap;
        }
        .hiw-prereq-text strong { color: #0a0a0a; font-weight: 500; letter-spacing: -0.01em; }
        .hiw-prereq-text span { color: rgba(0,0,0,0.5); }
        .hiw-prereq-text span::before { content: '·'; margin-right: 0.5rem; color: rgba(0,0,0,0.25); }
        .hiw-prereq-text a { color: #0a0a0a; text-decoration: underline; text-underline-offset: 2px; }
        @media (max-width: 640px) {
          .hiw-prereq-text { flex-direction: column; white-space: normal; gap: 0; }
          .hiw-prereq-text span::before { display: none; }
        }

        /* --- step flow: horizontal timeline with connector line --- */
        .hiw-flow {
          list-style: none;
          padding: 0;
          margin: 0 0 clamp(3rem, 6vh, 4.5rem);
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 0.625rem;
          position: relative;
        }
        .hiw-flow-line {
          position: absolute;
          top: calc(clamp(1.25rem, 1.8vw, 1.5rem) + 9px);
          left: calc(clamp(1.25rem, 1.8vw, 1.5rem) + 18px);
          right: calc(clamp(1.25rem, 1.8vw, 1.5rem) + 18px);
          height: 1px;
          background: repeating-linear-gradient(
            90deg,
            rgba(0,0,0,0.14) 0 3px,
            transparent 3px 8px
          );
          pointer-events: none;
          z-index: 0;
        }
        @media (max-width: 1100px) {
          .hiw-flow { grid-template-columns: repeat(3, 1fr); }
          .hiw-flow-line { display: none; }
        }
        @media (max-width: 700px)  {
          .hiw-flow { grid-template-columns: 1fr; gap: 0.5rem; }
        }

        .hiw-head, .hiw-card, .hiw-prereq {
          opacity: 0;
          translate: 0 14px;
          transition: opacity 600ms cubic-bezier(0.16, 1, 0.3, 1), translate 600ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .hiw.in-view .hiw-head { opacity: 1; translate: 0 0; }
        .hiw.in-view .hiw-prereq { opacity: 1; translate: 0 0; transition-delay: 80ms; }
        .hiw.in-view .hiw-card {
          opacity: 1; translate: 0 0;
          transition-delay: calc(160ms + var(--i, 0) * 80ms);
        }

        .hiw-card {
          position: relative;
          z-index: 1;
          background: #ffffff;
          border: 1px solid rgba(0,0,0,0.07);
          border-radius: 14px;
          padding: clamp(1.25rem, 1.8vw, 1.5rem);
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          transition:
            transform 260ms cubic-bezier(0.16, 1, 0.3, 1),
            border-color 200ms ease,
            box-shadow 260ms ease,
            opacity 600ms cubic-bezier(0.16, 1, 0.3, 1),
            translate 600ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .hiw-card:hover {
          transform: translateY(-2px);
          border-color: rgba(0,0,0,0.18);
          box-shadow: 0 10px 24px -14px rgba(0,0,0,0.1);
        }
        .hiw-card--last {
          background: #0a0a0a;
          border-color: #0a0a0a;
          color: #fff;
        }
        .hiw-card--last:hover {
          border-color: #27272a;
          box-shadow: 0 14px 30px -14px rgba(124, 58, 237, 0.35);
        }
        .hiw-card--last .hiw-num  { color: rgba(255,255,255,0.22); }
        .hiw-card--last .hiw-icon { background: rgba(255,255,255,0.08); color: #c4b5fd; border-color: rgba(255,255,255,0.1); }
        .hiw-card--last .hiw-step-title { color: #fff; }
        .hiw-card--last .hiw-step-sub   { color: rgba(255,255,255,0.68); }

        .hiw-card-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
        }
        .hiw-num {
          font-size: 0.875rem;
          font-weight: 500;
          color: rgba(0,0,0,0.28);
          letter-spacing: -0.01em;
          font-variant-numeric: tabular-nums;
          line-height: 1;
        }
        .hiw-icon {
          width: 26px; height: 26px;
          border-radius: 7px;
          background: #f5f5f4;
          border: 1px solid rgba(0,0,0,0.04);
          color: rgba(0,0,0,0.7);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: background 200ms ease, color 200ms ease;
        }
        .hiw-card:hover .hiw-icon {
          background: #ede9fe;
          color: #6d28d9;
        }
        .hiw-card--last:hover .hiw-icon {
          background: rgba(124, 58, 237, 0.25);
          color: #fff;
        }
        .hiw-step-title {
          font-size: 0.9375rem;
          font-weight: 500;
          letter-spacing: -0.02em;
          line-height: 1.3;
          margin: 0.125rem 0 0.35rem;
          color: #0a0a0a;
        }
        .hiw-step-sub {
          font-size: 0.8125rem;
          color: rgba(0,0,0,0.55);
          line-height: 1.5;
          margin: 0;
        }

        @media (max-width: 700px) {
          .hiw-card { padding: 1.125rem 1.25rem; }
        }

        /* ==========================================================
           INSTALL PREVIEW — visual mock of the Android install flow
           ========================================================== */
        .hiw-preview {
          background: #fafaf9;
          border: 1px solid #e9e9e7;
          border-radius: 14px;
          padding: clamp(1.5rem, 3vw, 2rem);
        }
        .hiw-preview-label {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #9b9a97;
          margin-bottom: 1.25rem;
        }
        .hiw-preview-label svg { color: #7c3aed; }

        /* ==========================================================
           SETUP CARD — Notion-style permissions checklist
           ========================================================== */
        .hiw-setup-card {
          max-width: 720px;
          margin: 0 auto;
          background: #ffffff;
          border: 1px solid #e9e9e7;
          border-radius: 12px;
          overflow: hidden;
        }
        .hiw-setup-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 14px 18px;
          border-bottom: 1px solid #e9e9e7;
          background: #ffffff;
        }
        .hiw-setup-title {
          font-size: 14px;
          font-weight: 600;
          color: #37352f;
          letter-spacing: -0.01em;
          line-height: 1.2;
        }
        .hiw-setup-sub {
          font-size: 12px;
          color: #9b9a97;
          margin-top: 3px;
        }
        .hiw-setup-progress {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
        }
        .hiw-setup-progress-track {
          width: clamp(60px, 10vw, 90px);
          height: 3px;
          background: #f0efed;
          border-radius: 999px;
          overflow: hidden;
        }
        .hiw-setup-progress-fill {
          height: 100%;
          background: #7c3aed;
          border-radius: 999px;
          transition: width 420ms cubic-bezier(0.65, 0, 0.35, 1);
        }
        .hiw-setup-progress-label {
          font-size: 12px;
          font-weight: 500;
          color: #787774;
          font-variant-numeric: tabular-nums;
          min-width: 28px;
          text-align: right;
        }

        /* Checklist */
        .hiw-checklist {
          list-style: none;
          padding: 4px 0;
          margin: 0;
          display: flex;
          flex-direction: column;
        }
        .hiw-check {
          display: grid;
          grid-template-columns: 28px 1fr auto auto;
          align-items: center;
          gap: 12px;
          padding: 12px 18px;
          position: relative;
          transition: background 180ms ease;
        }
        .hiw-check + .hiw-check {
          border-top: 1px solid #f0efed;
        }
        .hiw-check-icon {
          width: 28px; height: 28px;
          border-radius: 7px;
          background: #f7f6f3;
          color: #787774;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: background 280ms ease, color 280ms ease;
        }
        .hiw-check-body { min-width: 0; }
        .hiw-check-title {
          font-size: 13.5px;
          font-weight: 500;
          color: #37352f;
          letter-spacing: -0.005em;
          line-height: 1.3;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .hiw-check-sub {
          font-size: 12px;
          color: #9b9a97;
          margin-top: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .hiw-check-time {
          font-size: 11.5px;
          color: #9b9a97;
          font-variant-numeric: tabular-nums;
          min-width: 32px;
          text-align: right;
        }

        /* Status indicator — ring (pending), spinner (active), check (done) */
        .hiw-check-status {
          position: relative;
          width: 18px; height: 18px;
          flex-shrink: 0;
        }
        .hiw-check-ring,
        .hiw-check-spinner,
        .hiw-check-done {
          position: absolute;
          inset: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
        }
        .hiw-check-ring {
          border: 1.5px solid #d3d2cf;
          opacity: 1;
          transition: opacity 200ms ease;
        }
        .hiw-check-spinner {
          border: 1.5px solid rgba(124, 58, 237, 0.18);
          border-top-color: #7c3aed;
          opacity: 0;
          animation: hiw-spin 0.9s linear infinite;
          transition: opacity 200ms ease;
        }
        @keyframes hiw-spin { to { transform: rotate(360deg); } }
        .hiw-check-done {
          background: #0f7b3e;
          color: #fff;
          opacity: 0;
          transform: scale(0.6);
          transition:
            opacity 180ms ease,
            transform 280ms cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        /* Active state: spinner visible, icon purple, row subtly tinted */
        .hiw-check--active .hiw-check-ring    { opacity: 0; }
        .hiw-check--active .hiw-check-spinner { opacity: 1; }
        .hiw-check--active .hiw-check-icon    { background: #f3eeff; color: #7c3aed; }
        .hiw-check--active                    { background: #faf8ff; }

        /* Done state: muted green check, icon green-tinted, row neutral */
        .hiw-check--done .hiw-check-ring    { opacity: 0; }
        .hiw-check--done .hiw-check-spinner { opacity: 0; }
        .hiw-check--done .hiw-check-done    { opacity: 1; transform: scale(1); }
        .hiw-check--done .hiw-check-icon    { background: #edf3ec; color: #0f7b3e; }

        /* ==========================================================
           CONNECTION DIAGRAM — device ↔ cloud with packet flow
           ========================================================== */
        .hiw-conn {
          display: grid;
          grid-template-columns: 1fr 1.1fr 1fr;
          align-items: center;
          gap: 12px;
          padding: 16px 18px 18px;
          border-top: 1px solid #e9e9e7;
          background: #fafaf9;
        }
        .hiw-conn-node {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }
        .hiw-conn-node--cloud { justify-content: flex-end; }
        .hiw-conn-icon {
          width: 32px; height: 32px;
          border-radius: 8px;
          background: #ffffff;
          color: #787774;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border: 1px solid #e9e9e7;
        }
        .hiw-conn-icon--tl {
          background: #ffffff;
          border-color: #e9e9e7;
        }
        .hiw-conn-logo {
          width: 18px;
          height: 18px;
          object-fit: contain;
          /* Logo art stays solid black on the white tile — no filter needed. */
        }
        .hiw-conn-text { min-width: 0; }
        .hiw-conn-label {
          font-size: 12.5px;
          font-weight: 600;
          color: #37352f;
          letter-spacing: -0.005em;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .hiw-conn-meta {
          font-size: 11.5px;
          color: #9b9a97;
          margin-top: 3px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .hiw-conn-status {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          color: #0f7b3e;
          font-weight: 500;
        }
        .hiw-conn-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: #0f7b3e;
          opacity: 0.95;
          animation: hiw-conn-pulse 2.2s ease-in-out infinite;
        }
        @keyframes hiw-conn-pulse {
          0%, 100% { opacity: 0.95; }
          50%      { opacity: 0.5; }
        }

        /* Wire + flowing packets — flat purple line, simple dots */
        .hiw-conn-wire {
          position: relative;
          height: 1px;
          background: #d3d2cf;
          overflow: visible;
        }
        .hiw-conn-packet {
          position: absolute;
          top: 50%;
          width: 6px; height: 6px;
          margin-top: -3px;
          border-radius: 50%;
          background: #7c3aed;
          opacity: 0;
          animation: hiw-packet-flow 2.4s cubic-bezier(0.65, 0, 0.35, 1) infinite;
        }
        .hiw-conn-packet-1 { animation-delay: 0s; }
        .hiw-conn-packet-2 { animation-delay: 0.8s; }
        .hiw-conn-packet-3 { animation-delay: 1.6s; }
        @keyframes hiw-packet-flow {
          0%   { left: -4px;  opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { left: calc(100% - 2px); opacity: 0; }
        }

        @media (max-width: 560px) {
          .hiw-check-time { display: none; }
          .hiw-check { grid-template-columns: 24px 1fr auto; gap: 0.625rem; padding: 0.625rem 1rem; }
          .hiw-check-icon { width: 24px; height: 24px; border-radius: 7px; }
          .hiw-setup-head { padding: 0.875rem 1rem; }
          .hiw-conn { grid-template-columns: 1fr 1fr 1fr; gap: 0.5rem; padding: 0.875rem 1rem 1rem; }
          .hiw-conn-icon { width: 28px; height: 28px; }
        }

        .hiw-preview-caption {
          font-size: 13px;
          color: #787774;
          line-height: 1.55;
          margin: 18px auto 0;
          max-width: 540px;
          text-align: center;
        }

        @media (prefers-reduced-motion: reduce) {
          .hiw-check-spinner,
          .hiw-conn-dot,
          .hiw-conn-packet {
            animation: none !important;
          }
          .hiw-conn-packet { opacity: 0; }
        }
        }
      `}</style>
    </section>
  );
}
